import { EventDetail } from './models/EventDetail.js';
import { EventListener } from './models/EventListener.js';
import { EventSource } from './models/EventSource.js';
import { EventSubscription } from './models/EventSubscription.js';
import { EventTypes } from './models/EventTypes.js';

export class SourceEventManager {
	public key: string;
	private subs: Map<EventTypes, EventSubscription<any, any>[]> = new Map();

	constructor(sourceKey: string) {
		this.key = sourceKey;
	}

	public getSubscribers<TType extends EventTypes, TDetail extends EventDetail>(type: TType): EventSubscription<TType, TDetail>[] {
		if (!this.subs || !this.isValidType(type)) {
			return [];
		}
		return (this.subs.get(type) as EventSubscription<TType, TDetail>[]) || [];
	}

	public subscribe<TType extends EventTypes, TDetail extends EventDetail>(subscription: EventSubscription<TType, TDetail>): void {
		const { type, listener } = subscription;
		if (!type || !this.isValidType(type) || !listener || !this.getListenerKey(listener)) {
			return;
		}
		const existingSubscribers = this.getSubscribers(type);
		const existingSubscription = existingSubscribers.find((existingSubscription) => this.getListenerKey(existingSubscription.listener) === this.getListenerKey(listener));
		if (!existingSubscription) {
			this.pushSubscribers(subscription);
		}
	}

	public unsubscribe<TType extends EventTypes, TDetail extends EventDetail>(type: TType, listener: EventListener): void {
		if (!this.isValidType(type)) {
			return;
		}
		const existingSubscribers = this.getSubscribers<TType, TDetail>(type);
		if (existingSubscribers.length > 0) {
			const updatedSubscribers = existingSubscribers.reduce((list: EventSubscription<TType, TDetail>[], item) => {
				if (this.getListenerKey(item.listener) !== this.getListenerKey(listener)) {
					list.push(item);
				}
				return list;
			}, []);
			this.setSubscribers(type, updatedSubscribers);
		}
	}

	public publish<TType extends EventTypes, TDetail extends EventDetail, TEventSource extends EventSource>(type: TType, detail: TDetail, source: TEventSource): void {
		if (!this.isValidType(type)) {
			console.error('SourceEventManager: Invalid Event Type', type);
			return;
		}
		const eventKey = this.getEventKey(type);
		const subscribers = this.getSubscribers(type);
		if (subscribers) {
			subscribers.forEach((subscriber) => {
				if (subscriber.listener.applicationId !== source.applicationId) {
					subscriber.onPublish({
						type,
						detail,
						source,
					});
				}
			});
		}
	}

	private pushSubscribers<TType extends EventTypes, TDetail extends EventDetail>(sub: EventSubscription<TType, TDetail>): void {
		const existing = this.getSubscribers(sub.type);
		this.subs.set(sub.type, [...existing, sub]);
	}

	private setSubscribers<TType extends EventTypes, TDetail extends EventDetail>(type: TType, subs: EventSubscription<TType, TDetail>[]): void {
		const workingSubs = subs.filter((sub) => sub.type === type);
		this.subs.set(type, workingSubs);
	}

	private isValidType(type: unknown): type is EventTypes {
		return Object.values(EventTypes).includes(type as EventTypes);
	}

	private getListenerKey(listener: EventListener) {
		return listener.applicationId;
	}

	private getEventKey<TType extends EventTypes>(type: TType) {
		return `${this.key}-${type}`;
	}
}